import React, { useMemo } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import {
  getOrthoMenuPageById,
  usesLeftJustifiedLayout,
  usesProfileLayout,
} from '../data/orthoMenuPages';
import { DR_APPROACH_VISUAL_STEP_IDS } from '../data/drApproachVisuals';
import {
  getOrthoPageHeroImage,
  getOrthoPageHeroVariant,
  isOrthoPageHeroTransparent,
  usesSplitHeroLayout,
} from '../data/orthoPageHeroImages';
import LowerFooter from '../Components/LowerFooter';
import { paths } from '../routes/paths';
import './LandingPages.css';

function SectionBlock({ title, paragraphs }) {
  return (
    <section className="landing_section">
      <h2 className="landing_h2">{title}</h2>
      {paragraphs.map((p) => (
        <p key={p.slice(0, 40)} className="landing_p">
          {p}
        </p>
      ))}
    </section>
  );
}

function PhilosophyTimelineStep({ stepKey, index, base, t }) {
  const title = t(`${base}.visualSteps.${stepKey}.title`);
  const text = t(`${base}.visualSteps.${stepKey}.text`);

  if (!title || title.startsWith(`${base}.visualSteps`)) {
    return null;
  }

  return (
    <li className="landing_philosophy_timeline_item">
      <div className="landing_philosophy_timeline_badge" aria-hidden="true">
        {index + 1}
      </div>
      <div className="landing_philosophy_timeline_body">
        <h3 className="landing_philosophy_timeline_title">{title}</h3>
        <p className="landing_philosophy_timeline_text">{text}</p>
      </div>
    </li>
  );
}

export default function OrthoMenuPage({ pageId }) {
  const { t } = useLocale();

  const pageMeta = useMemo(() => getOrthoMenuPageById(pageId), [pageId]);

  if (!pageMeta) {
    return <Navigate to={paths.home} replace />;
  }

  const base = `orthoPages.pages.${pageId}`;
  const h1 = t(`${base}.h1`);
  const intro = t(`${base}.intro`);
  const sectionKeys = ['s1', 's2', 's3'];
  const sections = sectionKeys
    .map((key) => {
      const title = t(`${base}.sections.${key}.title`);
      const p1 = t(`${base}.sections.${key}.p1`);
      if (!title || title === `${base}.sections.${key}.title`) return null;
      const paragraphs = [p1];
      const p2 = t(`${base}.sections.${key}.p2`);
      if (p2 && p2 !== `${base}.sections.${key}.p2`) paragraphs.push(p2);
      return { key, title, paragraphs };
    })
    .filter(Boolean);

  const benefitKeys = ['b1', 'b2', 'b3', 'b4'];
  const benefits = benefitKeys
    .map((key) => t(`${base}.benefits.${key}`))
    .filter((b) => b && !b.startsWith(`${base}.benefits`));

  const visualStepsHeading = t(`${base}.visualStepsHeading`);
  const hasVisualSteps =
    visualStepsHeading &&
    !visualStepsHeading.startsWith(`${base}.visualStepsHeading`) &&
    pageId === 'drApproach';

  const followUpSection = hasVisualSteps
    ? sections.filter((section) => section.title === t(`${base}.sections.s3.title`))
    : sections;

  const heroImage = getOrthoPageHeroImage(pageId);
  const heroImageAlt = t(`${base}.heroImageAlt`);
  const heroTransparent = isOrthoPageHeroTransparent(pageId);
  const heroVariant = getOrthoPageHeroVariant(pageId);
  const hasHeroImage =
    heroImage && heroImageAlt && !heroImageAlt.startsWith(`${base}.heroImageAlt`);
  const useProfileLayout = usesProfileLayout(pageId);
  const useLeftJustified = usesLeftJustifiedLayout(pageId);
  const useSplitLayout = usesSplitHeroLayout(pageId);

  return (
    <div
      className={`landing_page${useLeftJustified ? ' landing_page--left-justified' : ''}${
        useProfileLayout ? ' landing_page--profile-layout' : ''
      }${useSplitLayout ? ' landing_page--split-layout' : ''}${
        useSplitLayout && (heroTransparent || useLeftJustified)
          ? ' landing_page--split-transparent'
          : ''
      }`}
    >
      <header
        className={`landing_hero landing_hero_ortho${
          hasHeroImage ? ' landing_hero--split' : ''
        }${heroTransparent || useLeftJustified ? ' landing_hero--transparent' : ''}${
          useLeftJustified ? ' landing_hero--left-justified' : ''
        }`}
      >
        <div className={`landing_inner${hasHeroImage ? ' landing_hero_inner' : ''}`}>
          <div className={hasHeroImage || useLeftJustified ? 'landing_hero_text' : undefined}>
            <h1 className="landing_h1">{h1}</h1>
            <p className="landing_sub">{intro}</p>
            <div className="landing_ctas">
              <Link className="landing_primary" to={paths.reservas}>
                {t('orthoPages.common.bookCta')}
              </Link>
              <Link className="landing_secondary_link" to={paths.home}>
                {t('orthoPages.common.backHome')}
              </Link>
            </div>
            <div className="landing_trust">
              <span>{t('orthoPages.common.trust1')}</span>
              <span className="landing_dot">•</span>
              <span>{t('orthoPages.common.trust2')}</span>
              <span className="landing_dot">•</span>
              <span>{t('orthoPages.common.trust3')}</span>
            </div>
          </div>
          {hasHeroImage ? (
            <div className="landing_hero_media">
              <img
                className={`landing_hero_img${
                  heroTransparent ? ' landing_hero_img--transparent' : ''
                }${heroVariant === 'micro' ? ' landing_hero_img--micro' : ''}`}
                src={heroImage}
                alt={heroImageAlt}
                loading="eager"
              />
            </div>
          ) : null}
        </div>
      </header>

      <main
        className={`landing_inner landing_main${
          usesSplitHeroLayout(pageId) || useLeftJustified ? ' landing_main--half' : ''
        }${useLeftJustified ? ' landing_main--left-justified' : ''}`}
      >
        {hasVisualSteps ? (
          <section className="landing_philosophy_flow" aria-label={visualStepsHeading}>
            <header className="landing_philosophy_intro">
              <h2 className="landing_philosophy_heading">{visualStepsHeading}</h2>
            </header>
            <ol className="landing_philosophy_timeline">
              {DR_APPROACH_VISUAL_STEP_IDS.map((stepKey, index) => (
                <PhilosophyTimelineStep
                  key={stepKey}
                  stepKey={stepKey}
                  index={index}
                  base={base}
                  t={t}
                />
              ))}
            </ol>
          </section>
        ) : null}

        {(hasVisualSteps ? followUpSection : sections).map((section) => (
          <SectionBlock
            key={section.key}
            title={section.title}
            paragraphs={section.paragraphs}
          />
        ))}

        {benefits.length > 0 ? (
          <section className="landing_section">
            <h2 className="landing_h2">{t('orthoPages.common.benefitsTitle')}</h2>
            <ul className="landing_bullets">
              {benefits.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="landing_section landing_cta_block">
          <h2 className="landing_h2">{t('orthoPages.common.ctaTitle')}</h2>
          <p className="landing_p">{t('orthoPages.common.ctaText')}</p>
          <Link className="landing_primary" to={paths.reservas}>
            {t('orthoPages.common.bookCta')}
          </Link>
        </section>
      </main>

      <LowerFooter />
    </div>
  );
}
